import { Award, ShieldCheck, Heart, Users, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { BUSINESS, IMAGES } from "../data/site";
import FinalCTA from "../components/FinalCTA";

const credentials = [
  { icon: Award, text: "20+ years of aquatic education experience" },
  { icon: ShieldCheck, text: "Certified CPR & First Aid Instructor" },
  { icon: Heart, text: "Background in lifeguarding & water safety" },
  { icon: Users, text: "Experience teaching ages 6 months to adult" },
];

const philosophy = [
  {
    title: "Safety first, always.",
    body: "Every lesson begins and ends with safety. We build a strong foundation of water awareness before stroke mechanics ever enter the picture.",
  },
  {
    title: "Meet each student where they are.",
    body: "No two learners are the same. We adapt our pace, our tone, and our techniques to the personality and comfort of each child or adult.",
  },
  {
    title: "Build confidence, not fear.",
    body: "Water should feel like a friend, not a threat. We use patience, encouragement, and proven progressions to grow real confidence.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="aqua-section">
        <div className="aqua-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] -z-10" style={{ backgroundColor: "#EAF3FF" }} />
              <img
                src={IMAGES.founder}
                alt="Coach Félix Rivera"
                className="w-full h-[420px] sm:h-[500px] object-cover rounded-3xl shadow-lg"
                data-testid="about-founder-image"
              />
            </div>
          </motion.div>
          <div className="lg:col-span-7">
            <p className="text-eyebrow">About Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 text-[#0B2F6B] leading-tight">
              Meet Agua Flow Mobile.
            </h1>
            <p className="mt-5 text-[#3B5878] text-base sm:text-lg leading-relaxed">
              {BUSINESS.ownerName} is the founder, owner, and lead swim coach of
              Agua Flow Mobile. With more than {BUSINESS.yearsExperience} years
              of experience in aquatic education, lifeguarding, and water safety
              — and as a certified CPR/First Aid instructor — Félix has built a
              reputation for combining technical expertise with warmth, patience,
              and genuine care.
            </p>
            <p className="mt-4 text-[#3B5878] text-base sm:text-lg leading-relaxed">
              He started teaching swimming because of a simple truth he learned
              firsthand: water doesn't care how old you are. Drowning is one of
              the leading causes of death for young children — and yet it's also
              one of the most preventable. That's the heartbeat behind our
              mission: <strong className="text-[#0B2F6B]">saving lives, one lesson at a time.</strong>
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {credentials.map((c, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`about-credential-${i}`}>
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center mt-0.5" style={{ backgroundColor: "#EAF3FF" }}>
                    <c.icon className="w-4 h-4 text-[#1E7BE0]" />
                  </span>
                  <p className="text-[#0B2F6B] text-sm sm:text-base">{c.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <a href={BUSINESS.registrationUrl} target="_blank" rel="noopener noreferrer" data-testid="about-cta" className="btn-book">Book a Lesson Now</a>
            </div>
          </div>
        </div>
      </section>

      <section className="aqua-section pt-0">
        <div className="aqua-container">
          <div className="max-w-2xl mb-10">
            <p className="text-eyebrow">Teaching Philosophy</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 text-[#0B2F6B]">
              How we teach — and why it works.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {philosophy.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="card-soft p-7"
                data-testid={`philosophy-${i}`}
              >
                <CheckCircle2 className="w-6 h-6 text-[#1E7BE0]" />
                <h3 className="font-heading text-xl font-semibold mt-4 text-[#0B2F6B]">{p.title}</h3>
                <p className="mt-3 text-[#3B5878] leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
